'use strict';
import isArray from 'lodash/isArray';
import isUndefined from 'lodash/isUndefined';
import unionBy from 'lodash/unionBy';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {ReplaySubject} from 'rxjs/ReplaySubject';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/observable/empty';
import 'rxjs/add/observable/fromPromise';
import 'rxjs/add/operator/delay';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/share';
import React, {Component} from 'react';
import * as T from 'prop-types';
import {ViewMode} from '../../../core-utilities/enums/ViewMode';
import {FeedListView} from './FeedListView';

const PAGE_SIZE = 15;

function logErrorReturnNull(e) {
  console.error(e);
  return null;
}

function propsAreEqual(a, b) {
  if (!a && !b) {
    return true;
  } else if (!a || !b) {
    return false;
  } else {
    return a.id === b.id && a.collection === b.collection && a.type === b.type && a.init === b.init;
  }
}

function isOnline() {
  return isUndefined(navigator.onLine) ? true : navigator.onLine; // no way to know
}

function getErrorMode() {
  return isOnline() ? ViewMode.EOTHER : ViewMode.ENONET;
}

function sortByDate(o1, o2) {
  return (o2 && o2.date) - (o1 && o1.date);
}

export class FeedListComponent extends Component {
  static propTypes = {
    className: T.string,
    scrollClassName: T.string,
    transitionGroupClass: T.string,
    itemClassName: T.string,
    id: T.string.isRequired,
    collection: T.string.isRequired,
    type: T.string,
    /* isOwnFeed: This property allows us to not migrate existing activity feed entry msg codes by appending '.self' */
    isOwnFeed: T.bool,
    /* Enable lazy loading */
    init: T.bool,
  };

  static defaultProps = {
    type: 'feed',
    isOwnFeed: true,
    init: true,
  };

  static $inject = [
    'FirebaseUtil',
    'feedEntryService',
  ];

  constructor(props) {
    super(props);
    this.state = {
      mode: ViewMode.LOADING,
      data: [],
      hasMore: true,
    };

    this.handleLoadMore = this.handleLoadMore.bind(this);
    this.handleSelectSingle = this.handleSelectSingle.bind(this);
  }

  handleSelectSingle(evt, entry) {
    if (!this.props.isOwnFeed) {
      FeedListComponent.dependencies.feedEntryService.goToTarget(entry);
    }
  }

  render() {
    // We technically don't need flip move here but it takes care of the need
    // for an inner wrapping element for MeasuredChatView's inner Measure component
    // We can get rid of it if we rewrite ChatView to put the inner Measure around the smoothScroll ref
    return (
      <FeedListView
        className={this.props.className}
        scrollClassName={this.props.scrollClassName}
        transitionGroupClass={this.props.transitionGroupClass}
        itemClassName={this.props.itemClassName}
        list={this.state.data}
        mode={this.state.mode}
        isOwnFeed={this.props.isOwnFeed}
        onSelectSingle={this.handleSelectSingle}
        onLoadMore={this.handleLoadMore}
      />
    );
  }

  componentWillReceiveProps(nextProps) {
    if (!propsAreEqual(this.props, nextProps)) {
      setTimeout(() => this.props$ && this.props$.next(nextProps));
    }
  }

  componentDidMount() {
    this.props$ = new ReplaySubject(1);
    this.props$.next(this.props);

    if (this.props.init) {
      this.initData();
    } else {
      this.initSubscription = this.props$
        .filter(props => props.init)
        .take(1)
        .subscribe(this.initData.bind(this));
    }
  }

  initData() {
    // BehaviorSubject might be the wrong choice here
    this.loadMore$ = new BehaviorSubject(false);
    const updatedProps$ = this.props$
      .distinctUntilChanged(propsAreEqual)
      .share(); // hot observable

    const data$ = updatedProps$.switchMap(data => {
      // first request for a new view. subsequent events have a timestamp
      // also, clear the error if the previous attempt failed
      if (this.state.mode !== ViewMode.SUCCESS) {
        this.setState({
          mode: ViewMode.LOADING,
          data: [],
        });
      }
      this.lastId = null; // reset the firebase pagination ID

      return Observable.merge(
        this.createFeedObservable(data),
        this.loadMore$.concatMap(this.requestPage.bind(this)),
      )
        .scan((pages, p) => {
          const pLen = p.length;
          if (pLen > 0 && pLen < PAGE_SIZE) {
            this.setState({
              hasMore: false,
            });
          }
          this.lastId = pLen > 0 ? p[0].id : null;
          p.sort(sortByDate);
          return unionBy(pages, p, 'id').sort(sortByDate);
        }, []);
    });

    // TODO: onComplete should not happen and we should probably treat it as an error and resubscribe to the stream
    this.subscription = data$
      .delay(0)
      .subscribe(
        this.onNewData.bind(this),
        this.onLoadError.bind(this),
        console.warn.bind(console, 'on complete called')
      );
  }

  shouldComponentUpdate(nextProps, nextState) {
    return !propsAreEqual(this.props, nextProps)
      || this.state.data !== nextState.data
      || this.state.mode !== nextState.mode;
  }

  componentWillUnmount() {
    this.props$ = null;
    // cleanup calendar data observable
    this.loadMore$ && this.loadMore$.unsubscribe();
    this.subscription && this.subscription.unsubscribe();
    this.initSubscription && this.initSubscription.unsubscribe();
  }

  createFeedObservable(data) {
    // var self = this;
    return !data.collection || !data.id
      ? Observable.empty()
      : FeedListComponent.dependencies.FirebaseUtil.firebaseRef()
        .child(data.collection + '-meta')
        .child(data.id)
        .child('links')
        .child(data.type || 'feed')
        .limitToLast(PAGE_SIZE)
        .observe('value')
        .switchMap(this.hydrateMessages.bind(this));
  }

  hydrateMessages(ss) {
    const feedEntryService = FeedListComponent.dependencies.feedEntryService;
    const promises = [];

    ss.forEach((feedSS) => {
      const feedData = feedSS.val();
      feedData.id = feedSS.key();
      promises.push(feedEntryService.buildData(feedData, this.props).catch(logErrorReturnNull));
    });

    return Promise.all(promises)
      .then(list => list.filter(it => it !== null));
  }

  requestPage(deferred) {
    if (!deferred) {
      return Promise.resolve([]);
    } else {
      const promise = FeedListComponent.dependencies.FirebaseUtil.firebaseRef()
        .child(this.props.collection + '-meta')
        .child(this.props.id)
        .child('links')
        .child(this.props.type || 'feed')
        .orderByKey()
        .endAt(this.lastId)
        .limitToLast(PAGE_SIZE)
        .once('value')
        .then(this.hydrateMessages.bind(this));

      promise.then(deferred.resolve.bind(deferred));
      promise.catch(deferred.reject.bind(deferred));

      return Observable.fromPromise(promise);
    }
  }

  onNewData(data) {
    if (!isArray(data) || data.length === 0) {
      this.setState({
        mode: ViewMode.SUCCESS,
        data: [],
      });
    } else if (isArray(data)) {
      // TODO: combine data with master list?
      this.setState({
        mode: ViewMode.SUCCESS,
        data: data,
      });
    } else {
      // this shouldn't happen, but just in case, treat it as an error
      this.setState({
        mode: getErrorMode(),
        data: [],
      });
    }
  }

  onLoadError(error) {
    // TODO: this should not happen and we should probably treat it as an error and resubscribe to the stream
    console.warn('on error called', error);
    this.setState({
      mode: getErrorMode(),
      data: [],
    });
  }

  handleLoadMore() {
    if (this.loadMore$ && this.lastId && this.state.hasMore) {
      this.setState({
        mode: ViewMode.LOADING_MORE,
      });
      const deferred = Promise.defer();
      this.loadMore$.next(deferred);
      return deferred.promise;
    } else {
      return Promise.resolve(null);
    }
  }
}



// WEBPACK FOOTER //
// ./src/js/activity-feed/components/FeedList/FeedListComponent.js